import './VisualizadorArquivos.react.css';

import React, { useCallback, useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { getFileContentType, getFileExtension } from '../../libs/FileUtils';

import { Button } from '@elotech/components';
import PropTypes from 'prop-types';
import Spinner from '../Spinner.react.js';
import TableHeader from '../TableHeader.react.js';

const EXTENSOES_IMAGEM = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg'];

const EXTENSOES_VISUALIZAVEIS = [
  'pdf',
  'mp4',
  'mp3',
  'webm',
  'ogv',
  'ogg',
  ...EXTENSOES_IMAGEM
];

export const isArquivoVisualizavel = nomeArquivo =>
  EXTENSOES_VISUALIZAVEIS.includes(getFileExtension(nomeArquivo));

export const isArquivoImagem = nomeArquivo =>
  EXTENSOES_IMAGEM.includes(getFileExtension(nomeArquivo));

export const toArrayArquivos = arquivos => {
  if (!arquivos) {
    return [];
  }

  return typeof arquivos.toArray === 'function' ? arquivos.toArray() : arquivos;
};

const createObjectURL = (resposta, nomeArquivo) => {
  const conteudo =
    resposta && resposta.data !== undefined ? resposta.data : resposta;

  const blob =
    conteudo instanceof Blob
      ? conteudo
      : new Blob([conteudo], { type: getFileContentType(nomeArquivo) });

  return window.URL.createObjectURL(blob);
};

export const VisualizadorArquivos = ({
  arquivos,
  getNomeArquivo,
  getLegendaArquivo,
  keyExtractor,
  getUrlArquivo,
  carregarArquivo,
  permiteDownload,
  loading,
  emptyText,
  mensagemSemSelecao
}) => {
  const [arquivoSelecionado, setArquivoSelecionado] = React.useState();
  const [urlVisualizacao, setUrlVisualizacao] = React.useState();
  const [carregandoArquivo, setCarregandoArquivo] = React.useState(false);

  const podeVisualizarArquivo = useCallback(
    arquivo =>
      isArquivoVisualizavel(getNomeArquivo(arquivo)) &&
      (!getUrlArquivo || !!getUrlArquivo(arquivo)),
    [getNomeArquivo, getUrlArquivo]
  );

  const arquivosVisualizaveis = useMemo(
    () => toArrayArquivos(arquivos).filter(podeVisualizarArquivo),
    [arquivos, podeVisualizarArquivo]
  );

  useEffect(() => {
    if (!arquivoSelecionado && arquivosVisualizaveis.length) {
      setArquivoSelecionado(arquivosVisualizaveis[0]);
    }
  }, [arquivosVisualizaveis, arquivoSelecionado]);

  useEffect(() => {
    if (!arquivoSelecionado) {
      return undefined;
    }

    if (getUrlArquivo) {
      const url = getUrlArquivo(arquivoSelecionado);
      setCarregandoArquivo(!!url);
      setUrlVisualizacao(url);
      return undefined;
    }

    if (!carregarArquivo) {
      return undefined;
    }

    let url;
    let cancelado = false;

    setUrlVisualizacao(undefined);
    setCarregandoArquivo(true);

    Promise.resolve(carregarArquivo(arquivoSelecionado))
      .then(resposta => {
        if (cancelado) {
          return;
        }

        url = createObjectURL(resposta, getNomeArquivo(arquivoSelecionado));

        setUrlVisualizacao(url);
        if (!url) {
          setCarregandoArquivo(false);
        }
      })
      .catch(() => {
        if (!cancelado) {
          setUrlVisualizacao(undefined);
          setCarregandoArquivo(false);
        }
      });

    return () => {
      cancelado = true;

      if (url) {
        window.URL.revokeObjectURL(url);
      }
    };
  }, [arquivoSelecionado]);

  const navegar = useCallback(
    avancar => {
      if (!arquivosVisualizaveis.length) {
        return;
      }

      let proximoIndice =
        arquivosVisualizaveis.indexOf(arquivoSelecionado) + (avancar ? 1 : -1);

      if (proximoIndice >= arquivosVisualizaveis.length) {
        proximoIndice = 0;
      }

      if (proximoIndice < 0) {
        proximoIndice = arquivosVisualizaveis.length - 1;
      }

      setArquivoSelecionado(arquivosVisualizaveis[proximoIndice]);
    },
    [arquivosVisualizaveis, arquivoSelecionado]
  );

  const baixarArquivoSelecionado = useCallback(() => {
    if (!arquivoSelecionado) {
      return;
    }

    if (!urlVisualizacao) {
      return;
    }

    const link = document.createElement('a');

    link.href = urlVisualizacao;
    link.download = getNomeArquivo(arquivoSelecionado) || '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [arquivoSelecionado, urlVisualizacao, getNomeArquivo]);

  const getCaminhoVisualizacao = () => {
    if (!urlVisualizacao) {
      return 'about:blank';
    }

    if (getFileExtension(getNomeArquivo(arquivoSelecionado)) === 'pdf') {
      const parametros = ['zoom=page-width'];
      const separador = urlVisualizacao.includes('#') ? '&' : '#';

      if (!permiteDownload) {
        parametros.unshift('toolbar=0');
      }

      return `${urlVisualizacao}${separador}${parametros.join('&')}`;
    }

    return urlVisualizacao;
  };

  const renderLinha = (arquivo, index) => {
    const nomeArquivo = getNomeArquivo(arquivo);
    const legenda = getLegendaArquivo && getLegendaArquivo(arquivo);
    const visualizavel = podeVisualizarArquivo(arquivo);
    const selecionado = arquivo === arquivoSelecionado;

    return (
      <tr
        key={keyExtractor(arquivo, index)}
        className={`${visualizavel ? 'visualizador-arquivos-linha' : ''}${
          selecionado ? ' active' : ''
        }`}
        onClick={
          visualizavel ? () => setArquivoSelecionado(arquivo) : undefined
        }
      >
        <td>
          <div>{nomeArquivo}</div>
          {legenda && (
            <small className="visualizador-arquivos-legenda">{legenda}</small>
          )}
          {!visualizavel && (
            <small className="visualizador-arquivos-legenda">
              Apenas download
            </small>
          )}
        </td>
      </tr>
    );
  };

  const renderVisualizacao = () => {
    if (!arquivoSelecionado) {
      return <div className="info">{mensagemSemSelecao}</div>;
    }

    const renderElement = () => {
      if (!urlVisualizacao) {
        return;
      }

      if (isArquivoImagem(getNomeArquivo(arquivoSelecionado))) {
        return (
          <img
            className="visualizador-arquivos-imagem"
            id="visualizador-arquivos-imagem"
            src={urlVisualizacao}
            alt={getNomeArquivo(arquivoSelecionado)}
            style={{
              visibility: carregandoArquivo ? 'hidden' : 'visible'
            }}
            key={urlVisualizacao}
            onLoad={() => setCarregandoArquivo(false)}
            onError={() => setCarregandoArquivo(false)}
          />
        );
      }
      return (
        <iframe
          className="visualizador-arquivos-iframe"
          id="visualizador-arquivos-iframe"
          title={getNomeArquivo(arquivoSelecionado)}
          src={getCaminhoVisualizacao()}
          key={urlVisualizacao}
          onLoad={() => setCarregandoArquivo(false)}
          onError={() => setCarregandoArquivo(false)}
          style={{ visibility: carregandoArquivo ? 'hidden' : 'visible' }}
        />
      );
    };

    return (
      <div style={{ position: 'relative', height: '100%' }}>
        {carregandoArquivo && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
              zIndex: 1
            }}
          >
            <Spinner contain />
          </div>
        )}
        {renderElement()}
      </div>
    );
  };

  return (
    <Row className="visualizador-arquivos-conteudo">
      <Col xs={12} md={4} className="visualizador-arquivos-coluna-lista">
        {loading ? (
          <Spinner />
        ) : (
          <div className="panel-table-outer visualizador-arquivos-lista">
            <table className="panel-table striped fancy">
              <TableHeader
                empty={!arquivosVisualizaveis.length}
                text={emptyText}
              >
                <tr>
                  <th>Documento</th>
                </tr>
              </TableHeader>
              <tbody>{arquivosVisualizaveis.map(renderLinha)}</tbody>
            </table>
          </div>
        )}
      </Col>
      <Col xs={12} md={8} className="visualizador-arquivos-coluna-visualizacao">
        <div className="visualizador-arquivos-navegacao">
          <Button
            iconPosition="left"
            type="button"
            className="btn module-color"
            id="botao-anterior"
            disabled={arquivosVisualizaveis.length < 2}
            onClick={() => navegar(false)}
          >
            <i className="fa fa-arrow-left" /> Anterior
          </Button>
          {permiteDownload && (
            <Button
              iconPosition="left"
              type="button"
              className="btn module-color"
              id="botao-baixar"
              disabled={!arquivoSelecionado}
              onClick={baixarArquivoSelecionado}
            >
              <i className="fa fa-download" /> Baixar
            </Button>
          )}
          <Button
            iconPosition="right"
            type="button"
            className="btn module-color"
            id="botao-proximo"
            disabled={arquivosVisualizaveis.length < 2}
            onClick={() => navegar(true)}
          >
            Próximo <i className="fa fa-arrow-right" />
          </Button>
        </div>
        <div className="visualizador-arquivos-visualizacao">
          {renderVisualizacao()}
        </div>
      </Col>
    </Row>
  );
};

VisualizadorArquivos.propTypes = {
  arquivos: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  getNomeArquivo: PropTypes.func,
  getLegendaArquivo: PropTypes.func,
  keyExtractor: PropTypes.func,
  getUrlArquivo: PropTypes.func,
  carregarArquivo: PropTypes.func,
  permiteDownload: PropTypes.bool,
  loading: PropTypes.bool,
  emptyText: PropTypes.string,
  mensagemSemSelecao: PropTypes.string
};

VisualizadorArquivos.defaultProps = {
  getNomeArquivo: arquivo =>
    typeof arquivo.get === 'function'
      ? arquivo.get('nomeArquivo')
      : arquivo.nomeArquivo,
  keyExtractor: (arquivo, index) => index,
  permiteDownload: true,
  loading: false,
  emptyText: 'Não há arquivos.',
  mensagemSemSelecao: 'Nenhum arquivo disponível para visualização.'
};

export default VisualizadorArquivos;
